import { useEffect, useRef, useState } from "react";
import AddressAutocomplete from "../../components/AddressAutocomplete";
import { BASE_URL } from "../../config";
import { COUNTRY_NAME_TO_CODE } from "../../utils/countries";
import { showError, showSuccess } from "../../utils/dialog";
import UnsavedChangesModal from "../UnsavedChangesModal";

const AddPayerModal = ({ isOpen, onClose, onPayerAdded }) => {
  const emptyAddress = {
    name: "",
    street: "",
    city: "",
    region: "",
    zip: "",
    country: "",
  };
  const formRef = useRef(null);
  const [address, setAddress] = useState(emptyAddress);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedPopup, setShowUnsavedPopup] = useState(false);
  const [errors, setErrors] = useState({});
  const [showValidationAlert, setShowValidationAlert] = useState(false);
  const validationTimerRef = useRef(null);

  const resetPayerForm = () => {
    if (formRef.current) formRef.current.reset();
    setAddress(emptyAddress);
    setIsDirty(false);
    setShowUnsavedPopup(false);
    setErrors({});
    setShowValidationAlert(false);
  };

  const handleClose = () => {
    setShowUnsavedPopup(false);
    onClose?.();
  };

  useEffect(() => {
    if (!isOpen || !isDirty) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isOpen, isDirty]);

  useEffect(() => {
    return () => {
      if (validationTimerRef.current) {
        clearTimeout(validationTimerRef.current);
      }
    };
  }, []);

  if (!isOpen) return null;

  const triggerValidationAlert = () => {
    setShowValidationAlert(true);
    if (validationTimerRef.current) {
      clearTimeout(validationTimerRef.current);
    }
    validationTimerRef.current = setTimeout(() => {
      setShowValidationAlert(false);
      validationTimerRef.current = null;
    }, 4000);
  };

  const validateForm = () => {
    if (!formRef.current) return;
    const formData = new FormData(formRef.current);
    const nextErrors = {};

    if (!String(formData.get("name") || "").trim()) {
      nextErrors.name = "This field is required";
    }
    if (!String(formData.get("legalName") || "").trim()) {
      nextErrors.legalName = "This field is required";
    }
    if (!String(formData.get("workerType") || "").trim()) {
      nextErrors.workerType = "This field is required";
    }
    if (!address?.street || !address?.country || !address?.region) {
      nextErrors.address = "This field is required";
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      triggerValidationAlert();
      return false;
    }

    setShowValidationAlert(false);
    return true;
  };

  const handleSubmit = async () => {
    if (!formRef.current || !validateForm()) return;
    const formData = new FormData(formRef.current);

    const countryCode =
      address.country?.length === 2
        ? address.country
        : COUNTRY_NAME_TO_CODE[address.country];

    if (!countryCode || countryCode.length !== 2) {
      setErrors((prev) => ({
        ...prev,
        address: "Country must be selected from the list",
      }));
      triggerValidationAlert();
      return;
    }

    const payload = {
      name: formData.get("name"),
      legalName: formData.get("legalName"),
      workerType: formData.get("workerType"),
      address: {
        name: address.name,
        street: address.street,
        city: address.city,
        region: address.region,
        zip: address.zip,
        country: countryCode,
      },
    };

    if (!address.region || address.region.length !== 2) {
      setErrors((prev) => ({
        ...prev,
        address: "Province / State must be selected from the list",
      }));
      triggerValidationAlert();
      return;
    }


    try {
      setIsSaving(true);
      const res = await fetch(`${BASE_URL}/api/accounting/payers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      

     
      if (!res.ok) {
        throw new Error("Failed to create payer");
      }

      showSuccess("Payer created successfully");
      resetPayerForm();
      onPayerAdded?.();
      handleClose();
    } catch (err) {
      console.error(err);
      showError("Failed to create payer");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveClick = () => {
    handleSubmit();
  };

  const handleAttemptClose = () => {
    if (isSaving) return;
    if (isDirty) {
      setShowUnsavedPopup(true);
      return;
    }
    handleClose();
  };

  const handleDiscardChanges = () => {
    resetPayerForm();
    handleClose();
  };

  const handleReviewChanges = () => {
    setShowUnsavedPopup(false);
  };

  const handleSaveAndContinue = async () => {
    setShowUnsavedPopup(false);
    await handleSubmit();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          handleAttemptClose();
        }
      }}
    >
      <div
        className="bg-white rounded-xl shadow-lg w-full max-w-lg p-6 relative"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleAttemptClose}
          className="absolute top-3 right-3 text-[#DC1F00] hover:bg-[#DC1F0033] p-3 rounded-full"
        >
          ✖
        </button>

        <h2 className="text-xl text-[#3A4149] font-semibold mb-4">Add Payer</h2>
        <div className="h-[1px] bg-[#a15d33] mb-4"></div>

        <form
          ref={formRef}
          onSubmit={(e) => e.preventDefault()}
          className="space-y-4"
        >
          {/* Name */}
          <div>
            <label
              className={`block text-sm mb-1 ${
                errors.name ? "text-red-500" : "text-[#00000080]"
              }`}
            >
              Name *
            </label>
            <input
              type="text"
              name="name"
              onChange={() => {
                setIsDirty(true);
                setErrors((prev) => ({ ...prev, name: "" }));
              }}
              className={`w-full rounded-lg px-3 py-2 focus:ring-1 focus:ring-red-400 ${
                errors.name ? "border border-red-500 text-red-500" : "border"
              }`}
            />
            {errors.name && (
              <p className="mt-1 text-xs text-red-500">{errors.name}</p>
            )}
          </div>

          {/* Legal Name */}
          <div>
            <label
              className={`block text-sm mb-1 ${
                errors.legalName ? "text-red-500" : "text-[#00000080]"
              }`}
            >
              Legal Name *
            </label>
            <input
              type="text"
              name="legalName"
              onChange={() => {
                setIsDirty(true);
                setErrors((prev) => ({ ...prev, legalName: "" }));
              }}
              className={`w-full rounded-lg px-3 py-2 focus:ring-1 focus:ring-red-400 ${
                errors.legalName
                  ? "border border-red-500 text-red-500"
                  : "border"
              }`}
            />
            {errors.legalName && (
              <p className="mt-1 text-xs text-red-500">{errors.legalName}</p>
            )}
          </div>

          {/* Worker Type */}
          <div>
            <label
              className={`block text-sm mb-1 ${
                errors.workerType ? "text-red-500" : "text-[#00000080]"
              }`}
            >
              Worker Type *
            </label>
            <select
              name="workerType"
              onChange={() => {
                setIsDirty(true);
                setErrors((prev) => ({ ...prev, workerType: "" }));
              }}
              className={`w-full rounded-lg px-3 py-2 focus:ring-1 focus:ring-red-400 ${
                errors.workerType
                  ? "border border-red-500 text-red-500"
                  : "border"
              }`}
            >
              <option value="">Select</option>
              <option value="Contractor">Contractor</option>
              <option value="Employee">
                Employee - Field Staff
              </option>
              <option value="Outsourced">Outsourced Agency</option>
            </select>
            {errors.workerType && (
              <p className="mt-1 text-xs text-red-500">{errors.workerType}</p>
            )}
          </div>

          {/* Address */}
          {/* Address */}
          <div>
            <label
              className={`block text-sm mb-1 ${
                errors.address ? "text-red-500" : "text-[#DC1F00]"
              }`}
            >
              Address *
            </label>

            <AddressAutocomplete
              value={address}
              onChange={(addr) => {
                setIsDirty(true);
                setErrors((prev) => ({ ...prev, address: "" }));
                if (!addr || typeof addr !== "object") {
                  setAddress({ ...emptyAddress });
                  return;
                }
                setAddress((prev) => ({
                  ...prev,
                  ...addr,
                  country: addr.country ?? prev.country ?? "",
                  region: addr.region ?? prev.region ?? "",
                  name: addr.name ?? prev.name ?? "",
                }));
              }}
            />
            {errors.address && (
              <p className="mt-1 text-xs text-red-500">{errors.address}</p>
            )}

          </div>

          {/* Actions */}
          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={handleAttemptClose}
              className="w-full border-2 border-[#DC1F00] text-[#DC1F00] hover:bg-[#DC1F001A] px-4 py-2 rounded-full"
            >
              Close
            </button>
            <button
              type="button"
              onClick={handleSaveClick}
              className="w-full bg-[#DC1F00] hover:bg-[#b81a00] text-white px-4 py-2 rounded-full"
            >
              Save
            </button>
          </div>
        </form>
        <UnsavedChangesModal
          open={showUnsavedPopup}
          onSave={handleSaveAndContinue}
          onDiscard={handleDiscardChanges}
          onReview={handleReviewChanges}
          saving={isSaving}
        />
        {showValidationAlert && (
          <div className="fixed bottom-5 right-5 z-[120] rounded bg-[#f47d6b] px-5 py-4 text-sm font-medium text-white shadow-xl">
            Please review errors highlighted in red
          </div>
        )}
      </div>
    </div>
  );
};

export default AddPayerModal;
